'use client';
import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import type { BookingState } from './BookingWizard';

interface Props {
  booking: BookingState;
  updateBooking: (u: Partial<BookingState>) => void;
  onNext: () => void;
  onBack: () => void;
}

const MARKETING_OPTIONS = [
  { id: 'newsletter', label: 'Maritzburg Golf Club Newsletter', sub: 'Monthly club news, results, and updates' },
  { id: 'promotions', label: 'Promotional Offers and Specials', sub: 'Exclusive discounts and seasonal deals' },
  { id: 'events', label: 'Event Notifications', sub: 'Upcoming competitions and special events' },
  { id: 'competition', label: 'Competition Updates', sub: 'Results, leaderboards, and draw sheets' },
];

const MEDIA_OPTIONS = [
  { id: 'social', label: 'Social Media', sub: 'Facebook, Instagram, and club social channels' },
  { id: 'website', label: 'Website Content', sub: 'maritzburggolfclub.co.za and related platforms' },
  { id: 'promotional', label: 'Promotional Material', sub: 'Brochures, flyers, and print media' },
  { id: 'marketing', label: 'Club Marketing Campaigns', sub: 'Advertising and sponsorship collateral' },
];

const REQUIRED_CONSENTS = [
  { id: 'rules', label: 'I accept the Club Rules and Regulations', required: true },
  { id: 'indemnity', label: 'I accept the Liability and Indemnity Conditions', required: true },
  { id: 'terms', label: 'I accept the Terms and Conditions of Play', required: true },
];

const TERMS_TEXT = `MARITZBURG GOLF CLUB — TERMS AND CONDITIONS OF PLAY

1. GENERAL RULES
All players must abide by the Rules of Golf as published by the R&A and USGA. Local rules apply as posted on the notice board and scorecard.

2. DRESS CODE
Smart casual golf attire is required at all times on the course and in the clubhouse. Collarless t-shirts, denim, and athletic wear are not permitted on the course.

3. PACE OF PLAY
Players are expected to maintain the pace of play appropriate to the course conditions. A four-ball should complete 18 holes in no more than 4 hours 30 minutes. Slow play may result in being asked to allow faster groups to play through.

4. COURSE ETIQUETTE
Players must repair pitch marks on greens, replace or fill divots on fairways, and rake bunkers after use. Mobile phones must be on silent mode during play.

5. CARTS
Golf carts must remain on designated cart paths where indicated. Cart users are responsible for any damage caused by negligent use. Carts are not permitted on tees or greens.

6. INDEMNITY
Maritzburg Golf Club, its members, staff, and officials accept no liability for any loss, damage, injury, or death sustained by any person while on club premises or on the golf course, howsoever caused. Players participate at their own risk.

7. PHOTOGRAPHY
Players grant Maritzburg Golf Club the right to photograph and record activities on club premises for promotional purposes unless otherwise indicated during the booking process.

8. DATA PROTECTION
Personal information collected during the booking process is processed in accordance with the Protection of Personal Information Act (POPIA). Your data will not be shared with third parties without your consent.

9. CANCELLATION POLICY
Cancellations made more than 24 hours before the booked tee time will receive a full refund. Cancellations within 24 hours of the tee time are non-refundable. No-shows forfeit the full booking amount.

10. DISPUTES
Any dispute arising from these terms shall be resolved in accordance with South African law and within the jurisdiction of the Pietermaritzburg High Court.

By accepting these terms, you confirm that you have read, understood, and agree to be bound by these conditions.`;

export default function ConsentStep({ booking, updateBooking, onNext, onBack }: Props) {
  const [marketing, setMarketing] = useState<Record<string, boolean>>(
    (booking.consentData as Record<string, boolean>) || {}
  );
  const [media, setMedia] = useState<Record<string, boolean>>({});
  const [required, setRequired] = useState<Record<string, boolean>>({});
  const [attempted, setAttempted] = useState(false);

  const allRequiredChecked = REQUIRED_CONSENTS.every((c) => required[c.id]);

  function handleNext() {
    setAttempted(true);
    if (!allRequiredChecked) return;
    updateBooking({ consentData: { ...marketing, ...media, ...required } });
    onNext();
  }

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
      <div className="xl:col-span-2 space-y-6">
        {/* Marketing Preferences */}
        <div className="bg-card border border-border rounded-2xl p-6 shadow-card">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="MegaphoneIcon" size={16} variant="outline" className="text-primary" />
            </div>
            <h2 className="text-lg font-bold text-foreground">Marketing Preferences</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-5">
            Help us keep you informed about what matters to you. All selections are optional and can be changed at any time.
          </p>
          <div className="space-y-3">
            {MARKETING_OPTIONS.map((opt) => (
              <label
                key={`mkt-${opt.id}`}
                className="flex items-start gap-3 p-3.5 rounded-xl border border-border hover:bg-muted/50 cursor-pointer transition-colors group"
              >
                <div className="mt-0.5 relative">
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={!!marketing[opt.id]}
                    onChange={(e) => setMarketing((prev) => ({ ...prev, [opt.id]: e.target.checked }))}
                  />
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-150 ${
                    marketing[opt.id] ? 'bg-primary border-primary' : 'border-border group-hover:border-primary/50'
                  }`}>
                    {marketing[opt.id] && <Icon name="CheckIcon" size={12} variant="outline" className="text-white" />}
                  </div>
                </div>
                <div>
                  <div className="text-sm font-semibold text-foreground">{opt.label}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{opt.sub}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Photography Consent */}
        <div className="bg-card border border-border rounded-2xl p-6 shadow-card">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
              <Icon name="CameraIcon" size={16} variant="outline" className="text-accent-foreground" />
            </div>
            <h2 className="text-lg font-bold text-foreground">Photography & Media Consent</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-5">
            Maritzburg Golf Club occasionally photographs and videos events and rounds for promotional purposes.
            Please indicate where you consent to your image being used.
          </p>
          <div className="space-y-3">
            {MEDIA_OPTIONS.map((opt) => (
              <label
                key={`media-${opt.id}`}
                className="flex items-start gap-3 p-3.5 rounded-xl border border-border hover:bg-muted/50 cursor-pointer transition-colors group"
              >
                <div className="mt-0.5 relative">
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={!!media[opt.id]}
                    onChange={(e) => setMedia((prev) => ({ ...prev, [opt.id]: e.target.checked }))}
                  />
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-150 ${
                    media[opt.id] ? 'bg-secondary border-secondary' : 'border-border group-hover:border-secondary/50'
                  }`}>
                    {media[opt.id] && <Icon name="CheckIcon" size={12} variant="outline" className="text-white" />}
                  </div>
                </div>
                <div>
                  <div className="text-sm font-semibold text-foreground">{opt.label}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{opt.sub}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Terms & Conditions */}
        <div className="bg-card border border-border rounded-2xl p-6 shadow-card">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-red-light flex items-center justify-center">
              <Icon name="DocumentTextIcon" size={16} variant="outline" className="text-red-light-foreground" />
            </div>
            <h2 className="text-lg font-bold text-foreground">Terms, Conditions & Indemnity</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Please read the following carefully before accepting. All three consents are required to complete your booking.
          </p>

          {/* Scrollable Terms */}
          <div className="h-48 overflow-y-auto bg-muted/40 rounded-xl border border-border p-4 mb-5 scrollbar-thin">
            <pre className="text-xs text-muted-foreground whitespace-pre-wrap font-sans leading-relaxed">{TERMS_TEXT}</pre>
          </div>

          {/* Required Checkboxes */}
          <div className="space-y-3">
            {REQUIRED_CONSENTS.map((consent) => (
              <label
                key={`req-${consent.id}`}
                className={`flex items-start gap-3 p-3.5 rounded-xl border-2 cursor-pointer transition-all duration-150 ${
                  required[consent.id]
                    ? 'border-secondary bg-green-light/30'
                    : attempted && !required[consent.id]
                    ? 'border-red-400 bg-red-light/30' :'border-border hover:border-secondary/40'
                }`}
              >
                <div className="mt-0.5">
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={!!required[consent.id]}
                    onChange={(e) => setRequired((prev) => ({ ...prev, [consent.id]: e.target.checked }))}
                  />
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-150 ${
                    required[consent.id] ? 'bg-secondary border-secondary' : 'border-border'
                  }`}>
                    {required[consent.id] && <Icon name="CheckIcon" size={12} variant="outline" className="text-white" />}
                  </div>
                </div>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-foreground">{consent.label}</div>
                  <div className="text-xs text-red-500 font-medium mt-0.5">Required</div>
                </div>
              </label>
            ))}
          </div>

          {attempted && !allRequiredChecked && (
            <div className="mt-4 flex items-center gap-2 text-red-500 text-sm">
              <Icon name="ExclamationCircleIcon" size={16} variant="outline" />
              <span>All three consents must be accepted to proceed with your booking.</span>
            </div>
          )}
        </div>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onBack}
            className="flex-1 py-3 rounded-xl border-2 border-border text-foreground font-semibold text-sm hover:bg-muted transition-colors flex items-center justify-center gap-2"
          >
            <Icon name="ChevronLeftIcon" size={16} variant="outline" />
            Back
          </button>
          <button
            type="button"
            onClick={handleNext}
            className="flex-1 btn-primary py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2"
          >
            Continue to Payment
            <Icon name="ArrowRightIcon" size={16} variant="outline" />
          </button>
        </div>
      </div>

      {/* Right Panel */}
      <div className="space-y-4">
        <div className="bg-card border border-border rounded-2xl p-5 shadow-card">
          <h3 className="font-semibold text-foreground text-sm mb-3 flex items-center gap-2">
            <Icon name="LockClosedIcon" size={16} variant="outline" className="text-primary" />
            POPIA Compliance
          </h3>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Your personal information is processed in accordance with the Protection of Personal Information Act (POPIA).
            Maritzburg Golf Club is committed to protecting your privacy and will not share your data without consent.
          </p>
        </div>

        <div className="bg-card border border-border rounded-2xl p-5 shadow-card">
          <h3 className="font-semibold text-foreground text-sm mb-3">Consent Status</h3>
          <div className="space-y-2">
            {REQUIRED_CONSENTS.map((c) => (
              <div key={`status-${c.id}`} className="flex items-center gap-2">
                <div className={`w-4 h-4 rounded-full flex items-center justify-center shrink-0 ${required[c.id] ? 'bg-secondary' : 'bg-border'}`}>
                  {required[c.id] && <Icon name="CheckIcon" size={10} variant="outline" className="text-white" />}
                </div>
                <span className={`text-xs ${required[c.id] ? 'text-foreground' : 'text-muted-foreground'}`}>
                  {c.label.replace('I accept the ', '')}
                </span>
              </div>
            ))}
          </div>
          <div className={`mt-4 px-3 py-2 rounded-lg text-xs font-semibold text-center ${
            allRequiredChecked ? 'bg-green-light text-green-light-foreground' : 'bg-red-light text-red-light-foreground'
          }`}>
            {allRequiredChecked ? '✓ All consents accepted' : 'Awaiting required consents'}
          </div>
        </div>
      </div>
    </div>
  );
}